<template>
  <div class="container mx-auto px-4 py-6">
    <!-- Plant Section Card -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-8">
      <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-4 py-3">
        <div class="flex items-center justify-between">
          <h2 class="font-semibold text-white text-lg">
            {{ $t('Mobile.MobilePlantDashboard.title') }}
          </h2>

          <div class="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-white">
<path fill-rule="evenodd" clip-rule="evenodd" d="M4.87171 1.58811C4.18324 1.9184 4.34293 2.75714 4.79289 3.20711L9.08579 7.5L7.5 9.08579L3.20711 4.79289C2.75711 4.3429 1.90944 4.19971 1.58419 4.87995C0.985604 6.1319 0.769329 7.54124 0.975579 8.9287C1.23113 10.6478 2.11708 12.2104 3.46105 13.3124C4.80502 14.4144 6.51089 14.977 8.24676 14.8908C8.89048 14.8588 9.52124 14.7385 10.1228 14.537L17.8787 22.2929C19.0503 23.4645 20.9497 23.4645 22.1213 22.2929L22.2929 22.1213C23.4645 20.9497 23.4645 19.0503 22.2929 17.8787L14.537 10.1228C14.7395 9.51835 14.8601 8.8844 14.8913 8.2375C14.9753 6.50151 14.4104 4.79638 13.3066 3.45387C12.2028 2.11135 10.6391 1.22746 8.91964 0.974169C7.53192 0.769745 6.12286 0.987876 4.87171 1.58811ZM12.0945 10.5087C12.5356 9.7995 12.793 8.98448 12.834 8.13802C12.8932 6.91285 12.4946 5.70946 11.7156 4.76198C10.9366 3.8145 9.83297 3.19069 8.61946 3.01193C8.21216 2.95193 7.80225 2.94351 7.39883 2.98462L10.5 6.08579C11.2811 6.86684 11.281 8.13317 10.5 8.91421L8.91421 10.5C8.13317 11.281 6.86684 11.281 6.08579 10.5L2.98467 7.39888C2.94336 7.8044 2.95209 8.21647 3.01295 8.62584C3.1933 9.8391 3.81856 10.9419 4.76707 11.7196C5.71557 12.4974 6.91949 12.8944 8.14458 12.8336C8.98967 12.7916 9.80314 12.5338 10.511 12.093L11.9946 13.5766L11.9924 13.5782L19.2929 20.8787C19.6834 21.2692 20.3166 21.2692 20.7071 20.8787L20.8787 20.7071C21.2692 20.3166 21.2692 19.6834 20.8787 19.2929L12.0945 10.5087Z" fill="currentColor"/>
</svg>
          </div>
        </div>
      </div>

      <div class="p-4">
        <!-- Plant Records -->
        <div class="space-y-2">
          <PlantRecordRow
            v-for="plant in plants"
            :key="plant.id"
            :plant="plant"
            :project="project"
            :selected-date="selectedDate"
            :data-test="`plant-${plant.id}-records`"
            :project-unarchived="projectsStore.project?.unarchived"
          />
        </div>

        <!-- Loading State -->
        <AppSpinner
          v-if="fetchDailyPlantsState === States.LOADING"
          class="flex w-full justify-center p-4"
        />

        <!-- Empty State -->
        <div
          v-else-if="plants.length === 0 && fetchDailyPlantsState === States.COMPLETE"
          class="text-center py-8"
          data-test="no-plant"
        >
          <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-8 h-8 text-gray-400">
<path fill-rule="evenodd" clip-rule="evenodd" d="M4.87171 1.58811C4.18324 1.9184 4.34293 2.75714 4.79289 3.20711L9.08579 7.5L7.5 9.08579L3.20711 4.79289C2.75711 4.3429 1.90944 4.19971 1.58419 4.87995C0.985604 6.1319 0.769329 7.54124 0.975579 8.9287C1.23113 10.6478 2.11708 12.2104 3.46105 13.3124C4.80502 14.4144 6.51089 14.977 8.24676 14.8908C8.89048 14.8588 9.52124 14.7385 10.1228 14.537L17.8787 22.2929C19.0503 23.4645 20.9497 23.4645 22.1213 22.2929L22.2929 22.1213C23.4645 20.9497 23.4645 19.0503 22.2929 17.8787L14.537 10.1228C14.7395 9.51835 14.8601 8.8844 14.8913 8.2375C14.9753 6.50151 14.4104 4.79638 13.3066 3.45387C12.2028 2.11135 10.6391 1.22746 8.91964 0.974169C7.53192 0.769745 6.12286 0.987876 4.87171 1.58811ZM12.0945 10.5087C12.5356 9.7995 12.793 8.98448 12.834 8.13802C12.8932 6.91285 12.4946 5.70946 11.7156 4.76198C10.9366 3.8145 9.83297 3.19069 8.61946 3.01193C8.21216 2.95193 7.80225 2.94351 7.39883 2.98462L10.5 6.08579C11.2811 6.86684 11.281 8.13317 10.5 8.91421L8.91421 10.5C8.13317 11.281 6.86684 11.281 6.08579 10.5L2.98467 7.39888C2.94336 7.8044 2.95209 8.21647 3.01295 8.62584C3.1933 9.8391 3.81856 10.9419 4.76707 11.7196C5.71557 12.4974 6.91949 12.8944 8.14458 12.8336C8.98967 12.7916 9.80314 12.5338 10.511 12.093L11.9946 13.5766L11.9924 13.5782L19.2929 20.8787C19.6834 21.2692 20.3166 21.2692 20.7071 20.8787L20.8787 20.7071C21.2692 20.3166 21.2692 19.6834 20.8787 19.2929L12.0945 10.5087Z" fill="currentColor"/>
</svg>
          </div>

          <p class="text-gray-500 font-medium">
            {{ $t('Mobile.MobilePlantDashboard.noPlantFound', { date: new Date(selectingDateInput).toLocaleDateString() }) }}
          </p>
        </div>
      </div>
    </div>

    <!-- Date Navigation Footer -->
    <Teleport
      v-if="firstLoad"
      to="#footer"
    >
      <div class="w-full bg-white border-t border-gray-200 p-4">
        <div class="max-w-md mx-auto flex items-center space-x-4 justify-center">
          <button
            type="button"
            class="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors duration-200"
            data-test="prev-day"
            @click="prevDate"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 text-gray-600">
  <path fill-rule="evenodd" d="M7.72 12.53a.75.75 0 010-1.06l7.5-7.5a.75.75 0 111.06 1.06L9.31 12l6.97 6.97a.75.75 0 11-1.06 1.06l-7.5-7.5z" clip-rule="evenodd" />
</svg>
          </button>

          <div class="mx-auto">
            <AppInput
              v-model="selectingDateInput"
              class="form-input"
              type="date"
              name="selected_date"
              data-test="select-day-input"
              @keyup.enter="setDay"
              @change="screenSize < ScreenSize.MD && setDay()"
            />
          </div>

          <button
            type="button"
            class="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors duration-200"
            data-test="next-day"
            @click="nextDate"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 text-gray-600">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import PlantRecordRow from '@/components/Mobile/MobilePlantDashboard/PlantRecordRow.vue';
import useDayQueryParam from '@/composables/useDayQueryParam';
import useScreenSize, { ScreenSize } from '@/composables/useScreenSize';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { dateAsYMD } from '@/helpers';
import { useNavStore } from '@/store/nav';
import { usePlantsStore } from '@/store/plants';
import { useProjectsStore } from '@/store/projects';
import { useWbsCodesStore } from '@/store/wbsCodes';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const props = defineProps<{
  projectId: string;
}>();

const queryParams = useRoute().query.day as string;
const selectingDateInput = ref(dateAsYMD(new Date()));
const selectedDate = useDayQueryParam();

if(queryParams) {
  const queryDate = new Date(queryParams);

  if(!isNaN(queryDate.getTime())) {
    selectingDateInput.value = dateAsYMD(queryDate);
  }
}

const projectsStore = useProjectsStore();
const wbsCodesStore = useWbsCodesStore();

wbsCodesStore.index(props.projectId);

const project = computed(() => {
  return projectsStore.models.find(parseInt(props.projectId));
});

const plantsStore = usePlantsStore();
const plantIds = ref([]);

const plants = computed(() => {
  return plantsStore.models.query().with('records', (query) => {
    query.where('day', selectingDateInput.value).with('wbsCode');
  }).whereId(plantIds.value).get();
});

const router = useRouter();

const {
  request: fetchDailyPlants,
  state: fetchDailyPlantsState,
  firstLoad,
} = useStoreApiAction(plantsStore.fetchDailyPlants);

watch(selectingDateInput, (newValue) => {
  if(!newValue) {
    selectingDateInput.value = dateAsYMD(new Date());
    selectedDate.value = new Date();
  } else {
    selectedDate.value = new Date(newValue);
    router.replace({ query: { day: `${newValue}` } });
    plantIds.value = [];

    fetchDailyPlants(props.projectId, { day: dateAsYMD(new Date(newValue)) }).then((dailyPlants) => {
      dailyPlants.forEach((plant) => {
        plantIds.value.push(plant.id);
      });
    });
  }
}, { immediate: true });

const nextDate = () => {
  const selected = new Date(selectingDateInput.value);

  selected.setDate(selected.getDate() + 1);
  selectingDateInput.value = dateAsYMD(selected);
};

const prevDate = () => {
  const selected = new Date(selectingDateInput.value);

  selected.setDate(selected.getDate() - 1);
  selectingDateInput.value = dateAsYMD(selected);
};

const setDay = () => {
  if(!isNaN(new Date(selectingDateInput.value).getTime())) {
    selectedDate.value = new Date(selectingDateInput.value);
  }
};

const { screenSize } = useScreenSize();
const navStore = useNavStore();

navStore.backRoute = { name: 'MobileProjectPage' };
</script>

<style scoped></style>
